import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import type { ReactNode } from 'react'
import { readPreference, resolveTheme, writePreference } from './theme'
import type { ThemePreference } from './theme'
import { ThemeContext } from './useTheme'

const query = '(prefers-color-scheme: dark)'

function subscribeSystem(onChange: () => void) {
  const mql = window.matchMedia(query)
  mql.addEventListener('change', onChange)
  return () => mql.removeEventListener('change', onChange)
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPref] = useState<ThemePreference>(readPreference)
  const systemDark = useSyncExternalStore(
    subscribeSystem,
    () => window.matchMedia(query).matches,
    () => false,
  )
  const theme = resolveTheme(preference, systemDark)

  useEffect(() => {
    document.documentElement.dataset.theme = theme
  }, [theme])

  const setPreference = useCallback((pref: ThemePreference) => {
    writePreference(pref)
    setPref(pref)
  }, [])

  const value = useMemo(() => ({ preference, theme, setPreference }), [preference, theme, setPreference])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
